import { useMutation } from '@apollo/client/react';

import { IIntegrationAuth, IIntegrationAuthOptionsInput, OauthIntegrationProviderEnum } from 'src/graphql/schema';
import {
  FinishOauthIntegrationAuthDocument,
  IFinishOauthIntegrationAuthMutationVariables,
} from 'src/graphql/actions/integration/finishOauthIntegrationAuth.mutation';
import {
  InitOauthIntegrationAuthDocument,
  IInitOauthIntegrationAuthMutationVariables,
} from 'src/graphql/actions/integration/initOauthIntegrationAuth.mutation';
import { ReinitOauthIntegrationAuthDocument } from 'src/graphql/actions/integration/reinitOauthIntegrationAuth.mutation';
import { IntegrationsDocument } from 'src/graphql/actions/integration/integrations.query';

const useIntegrationAuth = (provider: OauthIntegrationProviderEnum) => {
  const [initOauthIntegrationAuth] = useMutation(InitOauthIntegrationAuthDocument, {
    refetchQueries: [IntegrationsDocument],
  });
  const [reinitOauthIntegrationAuth] = useMutation(ReinitOauthIntegrationAuthDocument, {
    refetchQueries: [IntegrationsDocument],
  });
  const [finishOauthIntegrationAuth] = useMutation(FinishOauthIntegrationAuthDocument, {
    refetchQueries: [IntegrationsDocument],
  });

  const localStorageKeyPrefix = `IntegrationAuth.${provider}`;

  const openAuth = (integrationAuth: IIntegrationAuth, options?: IIntegrationAuthOptionsInput) => {
    localStorage.setItem(`${localStorageKeyPrefix}.stateToken`, integrationAuth.stateToken);

    if (options) {
      localStorage.setItem(`${localStorageKeyPrefix}.options`, JSON.stringify(options));
    }

    window.open(integrationAuth.url);
  };

  const initAuth = async (options?: IIntegrationAuthOptionsInput) => {
    const variables: IInitOauthIntegrationAuthMutationVariables = { provider };

    if (options) {
      variables.options = options;
    }

    const { data } = await initOauthIntegrationAuth({ variables });

    if (!data?.initOauthIntegrationAuth?.integrationAuth) {
      throw new Error(data?.initOauthIntegrationAuth?.errors?.join(', ') || 'Something went wrong');
    }

    openAuth(data.initOauthIntegrationAuth.integrationAuth, options);
  };

  const reinitAuth = async (integrationId: string, options?: IIntegrationAuthOptionsInput) => {
    const { data } = await reinitOauthIntegrationAuth({ variables: { integrationId } });

    if (!data?.reinitOauthIntegrationAuth?.integrationAuth) {
      throw new Error(data?.reinitOauthIntegrationAuth?.errors?.join(', ') || 'Something went wrong');
    }

    openAuth(data.reinitOauthIntegrationAuth.integrationAuth, options);
  };

  const finishAuth = async (code: string, state: string) => {
    const stateToken = localStorage.getItem(`${localStorageKeyPrefix}.stateToken`);

    if (!stateToken) throw new Error('State token not found');

    const options = localStorage.getItem(`${localStorageKeyPrefix}.options`);

    const variables: IFinishOauthIntegrationAuthMutationVariables = {
      provider,
      stateToken,
      clientPayload: {
        code,
        state,
      },
    };

    if (options) {
      variables.options = JSON.parse(options);
    }

    const { data } = await finishOauthIntegrationAuth({ variables });

    if (!data?.finishOauthIntegrationAuth?.success) {
      throw new Error(data?.finishOauthIntegrationAuth?.errors?.join(', ') || 'Something went wrong');
    }

    localStorage.removeItem(`${localStorageKeyPrefix}.stateToken`);
    localStorage.removeItem(`${localStorageKeyPrefix}.options`);

    return data;
  };

  return { initAuth, reinitAuth, finishAuth };
};

export default useIntegrationAuth;
